import React, { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import config from './config';
import { getOrganizationContext, getOrganizationHeaders } from './orgContext';
import ScheduleSessionsCalendar from './admin/components/ScheduleSessionsCalendar';
import type { SessionStatus, ScheduleSessionItem } from './admin/components/ScheduleSessionsPanel';
import { AdminBreadcrumbs } from './admin/components/AdminUiPrimitives';
import { AdminPageHeader } from './admin/components/AdminMasterDetail';

type SessionFilter = 'all' | 'upcoming' | 'past' | 'this-week' | 'this-month';
type SessionView = 'list' | 'calendar';

type MySessionItem = {
  id: number;
  classroomId?: number | null;
  classroomName?: string;
  teacherUserId?: number;
  teacherDisplayName?: string;
  studentUserId?: number | null;
  studentDisplayName?: string;
  title?: string;
  startDateTime: string;
  endDateTime: string;
  locationType?: 'InPerson' | 'Online';
  location?: string | null;
  meetingUrl?: string | null;
  status: ScheduleStatusLike;
  recurrenceType?: 'None' | 'Weekly';
  recurrenceEndDate?: string | null;
  targetType?: 'Classroom' | 'Students';
};

type ScheduleStatusLike = 'Scheduled' | 'InProgress' | 'Completed' | 'Cancelled' | 'Rescheduled' | 'NoShow';

const API_BASE_URL = `${config.apiBaseUrl}/api`;

const canShowSessionMeetingLink = (startDateTime?: string | null, endDateTime?: string | null): boolean => {
  if (!startDateTime || !endDateTime) {
    return false;
  }

  const start = new Date(startDateTime);
  const end = new Date(endDateTime);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return false;
  }

  const now = Date.now();
  const showFrom = start.getTime() - (5 * 60 * 1000);
  return now >= showFrom && now <= end.getTime();
};

const formatDate = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString();
};

const formatTimeRange = (start: string, end: string): string => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return `${start} - ${end}`;
  }

  return `${startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${endDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

const toCalendarItem = (item: MySessionItem): ScheduleSessionItem => ({
  id: item.id,
  scheduleId: item.id,
  organizationId: 0,
  classroomId: item.classroomId ?? null,
  classroomName: item.classroomName,
  teacherUserId: item.teacherUserId ?? 0,
  teacherDisplayName: item.teacherDisplayName,
  studentUserId: item.studentUserId ?? null,
  studentDisplayName: item.studentDisplayName,
  targetType: item.targetType || 'Students',
  startDateTime: item.startDateTime,
  endDateTime: item.endDateTime,
  status: (item.status as SessionStatus) || 'Scheduled',
  location: item.location,
  meetingUrl: item.meetingUrl,
  notes: null,
  isModified: false,
});

const MySessions: React.FC = () => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<MySessionItem[]>([]);
  const [filter, setFilter] = useState<SessionFilter>('all');
  const [teacherFilter, setTeacherFilter] = useState<string>('all');
  const [subjectFilter, setSubjectFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<SessionView>('list');

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadSessions = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = await getAccessTokenSilently();
        const response = await axios.get<MySessionItem[]>(`${API_BASE_URL}/schedules/my?upcomingOnly=false`, {
          headers: {
            Authorization: `Bearer ${token}`,
            ...getOrganizationHeaders(),
          },
        });

        if (!cancelled) {
          setItems(Array.isArray(response.data) ? response.data : []);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.response?.data?.message || err?.message || 'Unable to load your sessions.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadSessions();

    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently, isAuthenticated]);

  const orgName = useMemo(() => {
    const context = getOrganizationContext();
    return context?.organizationName || context?.organizationSlug || 'Exam Masters';
  }, []);
  const organizationContext = getOrganizationContext();

  const uniqueTeachers = useMemo(() => Array.from(new Set(items.map((item) => item.teacherDisplayName).filter(Boolean) as string[])).sort(), [items]);
  const uniqueSubjects = useMemo(
    () => Array.from(new Set(items.map((item) => (item.title || item.classroomName || '').trim()).filter(Boolean))).sort(),
    [items],
  );

  const filteredItems = useMemo(() => {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setHours(0, 0, 0, 0);
    startOfWeek.setDate(now.getDate() - now.getDay());

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);

    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);

    return items.filter((item) => {
      const startDate = new Date(item.startDateTime);
      const endDate = new Date(item.endDateTime);
      const subjectValue = (item.title || item.classroomName || '').trim();
      const teacherValue = item.teacherDisplayName || '';

      const matchesTimeFilter = (() => {
        switch (filter) {
          case 'upcoming':
            return endDate >= now;
          case 'past':
            return endDate < now;
          case 'this-week':
            return startDate >= startOfWeek && startDate <= endOfWeek;
          case 'this-month':
            return startDate >= startOfMonth && startDate <= endOfMonth;
          case 'all':
          default:
            return true;
        }
      })();

      const matchesTeacher = teacherFilter === 'all' || teacherValue === teacherFilter;
      const matchesSubject = subjectFilter === 'all' || subjectValue === subjectFilter;

      return matchesTimeFilter && matchesTeacher && matchesSubject;
    });
  }, [filter, items, subjectFilter, teacherFilter]);

  const calendarSessions = useMemo(() => filteredItems.map(toCalendarItem), [filteredItems]);

  if (!isAuthenticated) {
    return (
      <div className="text-center py-12">
        <h1 className="text-xl font-bold text-slate-900">My Sessions</h1>
        <p className="mt-2 text-sm text-slate-600">Please sign in to view your schedule.</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <AdminBreadcrumbs items={[
        { label: 'Dashboard', to: organizationContext?.organizationSlug ? `/${organizationContext.organizationSlug}/analytics` : '/analytics' },
        { label: 'My Sessions', isCurrent: true },
      ]} />
      <AdminPageHeader
        title="My Sessions"
        subtitle={`Full schedule for ${orgName}`}
        actions={[]}
      />

      <div>
          <div className="p-0 md:p-0">
            <div className="mb-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="flex flex-wrap gap-2">
                {(['all', 'upcoming', 'past', 'this-week', 'this-month'] as SessionFilter[]).map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setFilter(value)}
                    className={[
                      'rounded-lg border px-3 py-2 text-sm font-semibold transition',
                      filter === value
                        ? 'border-indigo-600 bg-indigo-600 text-white'
                        : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100',
                    ].join(' ')}
                  >
                    {value === 'all' ? 'All' : value === 'upcoming' ? 'Upcoming' : value === 'past' ? 'Past' : value === 'this-week' ? 'This week' : 'This month'}
                  </button>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={teacherFilter}
                  onChange={(event) => setTeacherFilter(event.target.value)}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none"
                  aria-label="Filter by teacher"
                >
                  <option value="all">All teachers</option>
                  {uniqueTeachers.map((teacher) => (
                    <option key={teacher} value={teacher}>{teacher}</option>
                  ))}
                </select>

                <select
                  value={subjectFilter}
                  onChange={(event) => setSubjectFilter(event.target.value)}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none"
                  aria-label="Filter by subject"
                >
                  <option value="all">All subjects</option>
                  {uniqueSubjects.map((subject) => (
                    <option key={subject} value={subject}>{subject}</option>
                  ))}
                </select>

                <button
                  type="button"
                  onClick={() => navigate(organizationContext?.organizationSlug ? `/${organizationContext.organizationSlug}/my-schedule` : '/my-schedule')}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
                >
                  Upcoming list
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('list')}
                  className={[
                    'rounded-lg border px-3 py-2 text-sm font-semibold',
                    viewMode === 'list' ? 'border-indigo-600 bg-indigo-50 text-indigo-700' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100',
                  ].join(' ')}
                >
                  List
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('calendar')}
                  className={[
                    'rounded-lg border px-3 py-2 text-sm font-semibold',
                    viewMode === 'calendar' ? 'border-indigo-600 bg-indigo-50 text-indigo-700' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100',
                  ].join(' ')}
                >
                  Calendar
                </button>
              </div>
            </div>

            {loading ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-slate-600">Loading sessions...</div>
            ) : error ? (
              <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-rose-700">{error}</div>
            ) : filteredItems.length === 0 ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-slate-600">No sessions match the selected filter.</div>
            ) : viewMode === 'calendar' ? (
              <ScheduleSessionsCalendar sessions={calendarSessions} />
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-slate-600">
                      <th className="py-2 text-left font-semibold">Date</th>
                      <th className="py-2 text-left font-semibold">Time</th>
                      <th className="py-2 text-left font-semibold">Subject</th>
                      <th className="py-2 text-left font-semibold">Topic</th>
                      <th className="py-2 text-left font-semibold">Teacher</th>
                      <th className="py-2 text-left font-semibold">Session Link</th>
                      <th className="py-2 text-left font-semibold">Status</th>
                      <th className="py-2 text-left font-semibold">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((item) => (
                      <tr key={item.id} className="border-b border-slate-100 align-top">
                        <td className="py-2 text-slate-700">{formatDate(item.startDateTime)}</td>
                        <td className="py-2 text-slate-700">{formatTimeRange(item.startDateTime, item.endDateTime)}</td>
                        <td className="py-2 text-slate-700">{item.title || item.classroomName || '—'}</td>
                        <td className="py-2 text-slate-700">{item.classroomName || item.title || '—'}</td>
                        <td className="py-2 text-slate-700">{item.teacherDisplayName || '—'}</td>
                        <td className="py-2 text-slate-700">
                          {canShowSessionMeetingLink(item.startDateTime, item.endDateTime) && item.meetingUrl ? (
                            <a href={item.meetingUrl} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">Join</a>
                          ) : (
                            item.locationType === 'Online' ? 'Online' : (item.location || 'In Person')
                          )}
                        </td>
                        <td className="py-2 text-slate-700">{item.status}</td>
                        <td className="py-2">
                          <button
                            type="button"
                            onClick={() => navigate(organizationContext?.organizationSlug ? `/${organizationContext.organizationSlug}/my-session/${item.id}?source=schedule` : `/my-session/${item.id}?source=schedule`)}
                            className="inline-flex items-center rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-indigo-700"
                          >
                            {item.status === 'InProgress' ? 'Join' : 'View'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
    </div>
  );
};

export default MySessions;
